import React, { useEffect, useState } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { finalize } from 'rxjs';

import { ManageContact, ManageOrganisationUnit } from '@store/common/organisation.model';
import { suppliersUnitsService } from '@store/suppliers/units';
import { supplierDetailsQuery } from '@store/suppliers/details';

import { ArrowBack } from '@icons/index';

import OperationalUnitForm from '@components/unitDetails/OperationalUnit.form';
import OptionWithAlertComponent from '@components/moreOptions/OptionWithAlert.component';
import ManageContactDrawer from '@components/unitDetails/ManageContact.drawer';
import { GlobalStatus } from '@store/common/globalStatus.enum';
import { suppliersUnitsDetailsQuery } from '@store/suppliers/units/details';
import { useObservable } from '@ngneat/react-rxjs';

interface ManageSupplierUnitScreenProps {
  isCreation?: boolean;
}

const ManageSupplierUnitScreen = (props: ManageSupplierUnitScreenProps) => {
  const { isCreation } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { supplierId, supplierUnitId } = useParams();

  const [loading, setLoading] = useState(!isCreation);

  const [openContact, setOpenContact] = useState<ManageContact | boolean>(false);

  const [{headOffice}] = useObservable(suppliersUnitsDetailsQuery.supplierUnitDetails$);

  const [supplierUnit, setSupplierUnit] = useState<ManageOrganisationUnit | undefined>(undefined);

    useEffect(() => {
        setSupplierUnit(headOffice);
    }, [headOffice]);

  if (!isCreation && !supplierUnit) return null;

  const handleClose = () => {
    navigate('..');
  };

  const handleSave = (supplierUnit) => {
    if (isCreation) {
      if (!supplierId) return;
      setLoading(true);
      suppliersUnitsService.createSupplierUnit(supplierId, supplierUnit).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: (su) => {
            enqueueSnackbar(t('unitDetails.success.operationalUnit.create'), { variant: 'success' });
            navigate(`../${su.id}`, { replace: true });
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else {
      if (!supplierUnitId) return;
      setLoading(true);
      suppliersUnitsService.updateSupplierUnitById(supplierUnitId, supplierUnit).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: (su) => {
            enqueueSnackbar(t('unitDetails.success.operationalUnit.edit'), { variant: 'success' });
            setSupplierUnit(su);
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  };

  const handleDeleteContact = (contactId, successCallback) => {
    if (!supplierUnitId || !contactId) return;
    setLoading(true);
    suppliersUnitsService.deleteSuppliersUnitContact(supplierUnitId, contactId).pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: (cu) => {
          enqueueSnackbar(t('delete.success.contact'), { variant: 'success' });
          setSupplierUnit(cu);
          successCallback();
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  const isArchived = supplierDetailsQuery.isArchived || supplierUnit?.status === GlobalStatus.ARCHIVED;

  return (
    <>
      <Stack flex={1} width="100%" pt="14px" px="24px" spacing={1} overflow="hidden">
          {isCreation && (
              <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1} ml="-9px">
                  <IconButton onClick={handleClose}>
                      <ArrowBack/>
                  </IconButton>
                  <Typography fontSize="30px" fontWeight="700">
                      {isCreation ? t('suppliers.details.supplierUnits.newSupplierUnit') : supplierUnit?.name}
                  </Typography>
              </Stack>
          )}
        <OperationalUnitForm
          readOnly={isArchived}
          handleCancel={handleClose}
          handleSave={handleSave}
          handleManageContact={setOpenContact}
          handleDeleteContact={handleDeleteContact}
          isSupplierUnit
          moreOptions={[
            <OptionWithAlertComponent
              key="manage status"
              alertTitle={t(`${isArchived ? 'unarchive' : 'archive'}.title`)}
              alertDescription={t(`${isArchived ? 'unarchive' : 'archive'}.description.supplierUnit`)}
              validateTitle={t(`global.${isArchived ? 'unarchive' : 'archive'}`)}
              handleSuccess={() => {
                enqueueSnackbar(t(`${isArchived ? 'unarchive' : 'archive'}.success.supplierUnit`), { variant: 'success' });
                // updateClientUnit("status")(isArchived ? OrganisationStatus.ACTIVE : OrganisationStatus.ARCHIVED);
              }}
              handleValidate={supplierUnitId
                ? () => suppliersUnitsService.updateSuppliersUnitsStatus(supplierUnitId, isArchived ? GlobalStatus.ACTIVE : GlobalStatus.ARCHIVED)
                : undefined}
              label={t(`global.${isArchived ? 'unarchive' : 'archive'}`)} />,
            <OptionWithAlertComponent
              key="delete"
              alertTitle={t('delete.title')}
              alertDescription={t('delete.description.supplierUnit')}
              validateTitle={t('global.delete')}
              handleSuccess={() => {
                enqueueSnackbar(t('delete.success.supplierUnit'), { variant: 'success' });
                handleClose();
              }}
              handleValidate={supplierUnitId ? () => suppliersUnitsService.deleteSuppliersUnit(supplierUnitId) : undefined}
              label={t('global.delete')} />
          ]}
          unit={supplierUnit}
          loading={loading} />
      </Stack>
      {!!openContact && (
        <ManageContactDrawer
          contact={typeof openContact !== 'boolean' ? openContact : undefined}
          readOnly={isArchived}
          handleClose={() => setOpenContact(false)}
          handleAdd={(contact) => {
            if (!supplierUnitId) return;

            if (typeof openContact === 'boolean') {
              suppliersUnitsService.createSuppliersUnitContact(supplierUnitId, contact).subscribe({
                next: (cu) => {
                  enqueueSnackbar(t('unitDetails.success.contact.create'), { variant: 'success' });
                  setOpenContact(false);
                  setSupplierUnit(cu);
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
              });
            } else {
              if (!contact.id) return;
              suppliersUnitsService.updateSuppliersUnitContact(supplierUnitId, contact.id, contact).subscribe({
                next: (cu) => {
                  enqueueSnackbar(t('unitDetails.success.contact.edit'), { variant: 'success' });
                  setSupplierUnit(cu);
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
              });
            }
          }} />
      )}
    </>
  );
};

export default ManageSupplierUnitScreen;
